import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Table } from "reactstrap";

import { adminUserResets } from "../../../api/api.service";
import {
  CONTEXT_LABELS,
  RETURN_TO_TASK_LABELS,
  STATE_COLORS,
  STATE_LABELS,
  formatDateTime,
} from "./insightLabels";

const PAGE_SIZE = 20;

const yesNo = (value) => (value == null ? "—" : value ? "Yes" : "No");

/** Every reset this teacher started, newest first, with the outcome they logged. */
export default function Resets({ timezone }) {
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    adminUserResets(id, { page, limit: PAGE_SIZE })
      .then((res) => {
        if (!cancelled)
          setResult({ data: res?.data ?? [], total: res?.total ?? 0 });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, page]);

  const pages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
  // The moment is no longer asked when a reset starts; a column that reads
  // "Not recorded" on every row is noise, so it appears only when some row has one.
  const showMoment = result.data.some((row) => row.context);
  const columns = showMoment ? 7 : 6;
  const from = result.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, result.total);

  return (
    <section className="cd-panel cd-resets">
      <header className="cd-panel__head">
        <h3>Reset history</h3>
        <p>
          {result.total ? `${result.total} resets in total` : null}
          {result.total && timezone ? ` · times in ${timezone}` : null}
        </p>
      </header>

      {failed ? (
        <p className="cd-empty">
          Could not load the reset history. Try again in a moment.
        </p>
      ) : null}

      <div className={loading ? "is-loading" : ""}>
        <Table responsive className="cd-table mb-0">
          <thead>
            <tr>
              <th>When</th>
              <th>Classroom</th>
              <th>Reset</th>
              {showMoment ? <th>Moment</th> : null}
              <th>Back to work in</th>
              <th className="text-center">Usefulness</th>
              <th className="text-center">Extra support</th>
            </tr>
          </thead>
          <tbody>
            {result.data.map((row) => (
              <tr key={row.classActivityId}>
                <td className="text-nowrap">
                  {formatDateTime(row.startedAt, timezone)}
                </td>
                <td>{row.classroomName || "—"}</td>
                <td>
                  <span className="cd-reset-name">
                    <span
                      className="cd-dot"
                      style={{
                        background: STATE_COLORS[row.stateKey] || "#9aa59a",
                      }}
                    />
                    <span>
                      {row.activityTitle}
                      {row.stateKey ? (
                        <small>{STATE_LABELS[row.stateKey]}</small>
                      ) : null}
                    </span>
                  </span>
                </td>
                {showMoment ? (
                  <td>{CONTEXT_LABELS[row.context || "UNSPECIFIED"]}</td>
                ) : null}
                <td>
                  {row.returnToTaskBucket
                    ? RETURN_TO_TASK_LABELS[row.returnToTaskBucket]
                    : "—"}
                </td>
                <td className="text-center">
                  {row.usefulness != null ? `${row.usefulness}/10` : "—"}
                </td>
                <td className="text-center">{yesNo(row.additionalSupport)}</td>
              </tr>
            ))}
            {!result.data.length && !loading && !failed ? (
              <tr>
                <td colSpan={columns} className="cd-empty">
                  This teacher has not run a reset yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </div>

      {result.total > PAGE_SIZE ? (
        <nav className="cd-pager" aria-label="Reset history pages">
          <span>
            {from}–{to} of {result.total}
          </span>
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </nav>
      ) : null}
    </section>
  );
}
